// 📄 services/mesaAyudaService.ts
// Mesa de ayuda del proyecto (docs/plans/plan-tickets-proyecto.md, T2):
// tickets del día a día del cliente, distintos de los Incidentes
// (implementación) y de los tickets de plataforma (ticketService.ts).
// Se leen directo de Firestore (rules: miembros del proyecto; el cliente sin
// notas internas) y se escriben por onehubApi (/v1/projects/:id/tickets),
// que valida permisos, numera y manda los correos.
import { collection, onSnapshot, orderBy, query, where } from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import { db } from '../firebase';

// Reporte que adjunta la extensión de Chrome (D3 de
// docs/plans/plan-diagnostico-documentos.md). Lo escribe solo el backend.
export interface TicketReporteEvento {
  codigo: string | null;
  texto: string;
  fecha: string;
  autor: string;
}

export interface TicketReporte {
  plataforma: 'AN' | 'CIG';
  ambiente: 'test' | 'prd';
  buyerAnid: string | null;
  clienteId: string | null;
  urlPagina: string | null;
  // subtipo: tipo de mensaje de una transacción de CIG (StatusUpdateRequest...).
  documento: { tipo: string; numero: string; subtipo?: string | null; estatus: string; estatusNormalizado: string };
  eventos: TicketReporteEvento[];
  eventosLeidos: number | null;
  eventosTotales: number | null;
  coincidencias: { id: string; code: string; title: string }[];
  explicacionIa: { quePaso: string; porQue: string; queHacer: string; confianza: string | null } | null;
}

export const ESTADOS_TICKET = ['Abierto', 'En revisión', 'Esperando al cliente', 'Resuelto', 'Cerrado'] as const;
export type EstadoTicket = (typeof ESTADOS_TICKET)[number];
export const PRIORIDADES_TICKET = ['Baja', 'Media', 'Alta', 'Crítica'] as const;
export type PrioridadTicket = (typeof PRIORIDADES_TICKET)[number];

export interface TicketProyecto {
  id: string;
  numero: string;
  titulo: string;
  descripcion: string;
  prioridad: PrioridadTicket;
  estado: EstadoTicket;
  creadoPorUid: string;
  creadoPorNombre: string;
  creadoPorEmail: string;
  fechaCreacion?: any;
  // Solo se muestran al equipo (el cliente no sabe quién atiende).
  responsableUid: string | null;
  responsableNombre: string | null;
  origen: 'portal' | 'extension';
  reporte: TicketReporte | null;
  incidenteId: string | null;
  incidenteTitulo?: string;
  ultimoMensajeEn?: any;
  ultimoMensajePor: 'cliente' | 'equipo' | null;
}

export interface MensajeTicketProyecto {
  id: string;
  texto: string;
  autorUid: string;
  autorNombre: string;
  autorLado: 'cliente' | 'equipo';
  interno: boolean;
  creadoEn?: any;
}

// Colores compartidos por la Mesa de ayuda y la bandeja.
export const ESTADO_TICKET_BADGE: Record<EstadoTicket, string> = {
  'Abierto': 'bg-blue-500/10 text-blue-300 border-blue-500/20',
  'En revisión': 'bg-amber-500/10 text-amber-300 border-amber-500/20',
  'Esperando al cliente': 'bg-violet-500/10 text-violet-300 border-violet-500/20',
  'Resuelto': 'bg-emerald-500/10 text-emerald-300 border-emerald-500/20',
  'Cerrado': 'bg-white/5 text-gray-400 border-white/10'
};
export const PRIORIDAD_TICKET_COLOR: Record<PrioridadTicket, string> = {
  'Baja': 'text-gray-400', 'Media': 'text-yellow-300', 'Alta': 'text-orange-300', 'Crítica': 'text-red-400'
};

const ticketsRef = (projectId: string) => collection(db, 'projects', projectId, 'tickets');

export const escucharTicketsProyecto = (projectId: string, onCambio: (t: TicketProyecto[]) => void, onError: (err: Error) => void) =>
  onSnapshot(
    query(ticketsRef(projectId), orderBy('fechaCreacion', 'desc')),
    (snap) => onCambio(snap.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<TicketProyecto, 'id'>) }))),
    onError
  );

/** El cliente solo puede pedir los mensajes públicos (las rules rechazan otra query). */
export const escucharMensajesTicket = (
  projectId: string,
  ticketId: string,
  esCliente: boolean,
  onCambio: (m: MensajeTicketProyecto[]) => void,
  onError: (err: Error) => void
) => {
  const ref = collection(db, 'projects', projectId, 'tickets', ticketId, 'mensajes');
  const q = esCliente ? query(ref, where('interno', '==', false), orderBy('creadoEn', 'asc')) : query(ref, orderBy('creadoEn', 'asc'));
  return onSnapshot(q, (snap) => onCambio(snap.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<MensajeTicketProyecto, 'id'>) }))), onError);
};

const pedirApi = async <T,>(metodo: string, rutaV1: string, body?: object): Promise<T> => {
  const user = getAuth().currentUser;
  if (!user) throw new Error('No se detectó una sesión activa. Por favor, reingresa al sistema.');
  const res = await fetch(`${process.env.REACT_APP_API_URL}/v1${rutaV1}`, {
    method: metodo,
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${await user.getIdToken()}` },
    body: body === undefined ? undefined : JSON.stringify(body)
  });
  if (!res.ok) {
    const { error } = await res.json().catch(() => ({}));
    throw new Error(error || `Error ${res.status} al comunicarse con el servidor.`);
  }
  return res.json();
};

const pedir = <T,>(metodo: string, ruta: string, body?: object) => pedirApi<T>(metodo, `/projects${ruta}`, body);

export const crearTicketProyecto = (projectId: string, datos: { titulo: string; descripcion: string; prioridad: PrioridadTicket }) =>
  pedir<{ ticketId: string; number: string }>('POST', `/${encodeURIComponent(projectId)}/tickets`, {
    title: datos.titulo,
    description: datos.descripcion,
    priority: datos.prioridad
  });

export const enviarMensajeTicketProyecto = (projectId: string, ticketId: string, texto: string, interno: boolean) =>
  pedir('POST', `/${encodeURIComponent(projectId)}/tickets/${encodeURIComponent(ticketId)}/messages`, { text: texto, internal: interno });

export const actualizarTicketProyecto = (projectId: string, ticketId: string, cambios: { estado?: EstadoTicket; responsableUid?: string | null }) =>
  pedir('PATCH', `/${encodeURIComponent(projectId)}/tickets/${encodeURIComponent(ticketId)}`, {
    ...(cambios.estado !== undefined ? { status: cambios.estado } : {}),
    ...(cambios.responsableUid !== undefined ? { assigneeUid: cambios.responsableUid } : {})
  });

// T4: el incidente se crea con el formulario de siempre; esto solo los enlaza.
export const escalarTicketProyecto = (projectId: string, ticketId: string, incidenteId: string) =>
  pedir('POST', `/${encodeURIComponent(projectId)}/tickets/${encodeURIComponent(ticketId)}/escalation`, { incidentId: incidenteId });

// T5: bandeja "Tickets por atender" (contrato en inglés del backend).
export interface BandejaTicket {
  id: string;
  projectId: string;
  projectName: string;
  clientName: string;
  number: string;
  title: string;
  status: EstadoTicket;
  priority: PrioridadTicket;
  createdByName: string;
  createdAt: string | null;
  assigneeName: string | null;
  assignedToMe: boolean;
  unassigned: boolean;
  clientReplied: boolean;
}
export interface BandejaTickets {
  tickets: BandejaTicket[];
  summary: { total: number; unassigned: number; assignedToMe: number; clientReplied: number };
}
export const obtenerBandejaTickets = () => pedirApi<BandejaTickets>('GET', '/support-tickets/inbox');
